import type { ChatResponseContentType } from "../services/chatService";
import type { Widget } from "../types/widget";
import { isForecastResultPayload } from "./forecastResultModel";
import { normalizeTablePayload } from "./tablePayload";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Human-readable widget output format for draft panels and metadata. */
export function formatWidgetOutputLabel(draft: Pick<Widget, "output">): string {
  const format = (draft.output?.format || "echarts").toLowerCase();
  if (format === "echarts") {
    const mapping = draft.output?.mapping;
    if (mapping && typeof mapping === "object") {
      const chartType = (mapping as Record<string, unknown>).chartType;
      if (chartType != null && String(chartType).trim() !== "") {
        return `echarts (${String(chartType)})`;
      }
    }
    return "echarts";
  }
  return format;
}

export function isChartPayload(result: unknown): boolean {
  if (!isRecord(result)) return false;
  if (isRecord(result.option) && Array.isArray(result.option.series)) return true;
  if (Array.isArray(result.series)) return true;
  if (result.chartType != null && isRecord(result.option)) return true;
  return false;
}

/**
 * Pick the renderer branch from declared format and payload shape.
 */
export function resolveRenderType(
  type: ChatResponseContentType | "auto-selection" | "progress" | "warehouse-job" | "chat-run" | undefined,
  outputFormat: string | undefined,
  result: unknown
): ChatResponseContentType {
  if (type === "auto-selection" || type === "progress" || type === "warehouse-job" || type === "chat-run") {
    return "text";
  }
  const resolved = type ?? "text";
  if (resolved === "widget-draft" || resolved === "clarification" || resolved === "error" || resolved === "mutation-draft" || resolved === "forecast-plan") {
    return resolved;
  }

  // Prefer structured forecast/prediction UI over raw JSON dumps.
  if (isForecastResultPayload(result)) {
    return "forecast-result";
  }

  const fmt = outputFormat?.toLowerCase();
  const tablePayload = normalizeTablePayload(result);
  const htmlTableResponse =
    isRecord(result) &&
    typeof result.response === "string" &&
    result.response.trim().startsWith("<table");

  if (fmt === "table" || tablePayload || htmlTableResponse) return "table";
  if (fmt === "html") return "html";
  if (fmt === "json") return "json";
  if (fmt === "text") return "text";
  if (fmt === "echarts" || fmt === "chart" || isChartPayload(result)) return "chart";

  if (resolved === "chart" && tablePayload) return "table";
  return resolved;
}
